import { useMatchDevice } from '@avensia/excite';
import { styled } from '@glitz/react';
import { theme } from 'nordicnest/shared/theme';
import * as React from 'react';
import { useViewEventTrigger } from 'webapp/tracking';
import { addViewItemEventToDataLayer, ListType } from 'webapp/tracking/events/items';
import { Carousel, type DeviceSettingType, useCarousel } from 'webapp/components/Carousel';
import { isKitchentime } from 'webapp/theme';
import { DotButtons, Dots } from 'webapp/components/Carousel/Dots';
import { LoadingProductCard, ProductCard } from 'webapp/components/ProductCard';
import { sharedSpacing } from 'webapp/shared/sharedTheme';

type PropType = {
  id?: string;
  name?: string;
  products: NordicNest.Api.PartialProductApiModel[];
};

const visibleCount: DeviceSettingType = { mobile: 2, tablet: 3, desktop: 4 };

export default function ProductListCarousel(props: PropType) {
  const deviceMatch = useMatchDevice();
  const isSmallDevice = deviceMatch.isMobile || deviceMatch.isTablet;

  const self = React.createRef<HTMLDivElement>();
  useViewEventTrigger(
    self,
    addViewItemEventToDataLayer,
    {
      products: props.products,
      list_id: props.id,
      list_name: props.name,
      list_type: ListType.ProductListBlock,
      list_pagination_index: 1,
      list_pagination_max: 1,
    },
    props.products.map(x => x.sku).join(','),
  );

  const carousel = useCarousel(props.id, {
    containScroll: 'keepSnaps',
    align: isSmallDevice ? 'start' : 'center',
  });

  const nextSlideFactor: DeviceSettingType = React.useMemo(
    () => ({
      mobile: props.products.length > visibleCount.mobile ? 1.15 : 1,
      tablet: props.products.length > visibleCount.tablet ? 1.15 : 1,
      desktop: 1,
    }),
    [props.products.length],
  );

  return (
    <Base ref={self}>
      <Carousel
        visibleCount={visibleCount}
        gap={sharedSpacing.xs}
        showNextSlideFactor={nextSlideFactor}
        arrowNavigation={deviceMatch.isDesktop && isKitchentime}
        {...carousel}
      >
        {props.products?.map((product, index) => (
          <ProductCard
            list_id={props.id}
            list_name={props.name}
            list_type={ListType.ProductListBlock}
            key={product.url}
            product={product}
            index={index + 1}
            list_pagination_index={1}
            list_pagination_max={1}
          />
        ))}
      </Carousel>
      <DotButtons emblaApi={carousel.emblaApi} />
    </Base>
  );
}

const nextSlideFactorLoadingList: DeviceSettingType = {
  mobile: 1.5,
  tablet: 1.5,
  desktop: 1,
};

export function LoadingProductListCarousel() {
  const count = { mobile: 2, tablet: 3, desktop: 4 };
  const carousel = useCarousel(undefined, {});

  return (
    <Base>
      <Carousel
        visibleCount={count}
        gap={sharedSpacing.xs}
        showNextSlideFactor={nextSlideFactorLoadingList}
        {...carousel}
      >
        {new Array(count.desktop).fill(1).map(({}, i) => (
          <LoadingProductCard key={i} />
        ))}
      </Carousel>
      <Dots />
    </Base>
  );
}

const Base = styled.div();
